import { Link } from "react-router-dom";
import { ArrowRight, Video, ShieldCheck, MapPin } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn, SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    q: "Do you provide therapy throughout California?",
    a: "Yes. My practice is based in Long Beach, and as a California Licensed Clinical Social Worker I provide therapy to clients located anywhere in California via secure telehealth. You must be physically located in California at the time of your session.",
  },
  {
    q: "How does online couples therapy work?",
    a: "Sessions take place over a secure, HIPAA-conscious video platform. Both partners can join from the same room or from separate locations within California. Many couples find telehealth removes logistical barriers — no commute, no childcare scramble — and makes consistency far easier.",
  },
  {
    q: "Do both partners need to attend every session?",
    a: "Couples therapy works best when both partners attend regularly. Occasionally, an individual session with one partner may be clinically useful, and we would discuss that together rather than making it a default.",
  },
  {
    q: "How long are sessions?",
    a: "Standard sessions are 50 minutes. For couples and families, I recommend a 90-minute intake session so everyone has room to be fully heard from the very start.",
  },
  {
    q: "What is your fee?",
    a: "Individual therapy (virtual) is $220 per 50-minute session. Couples therapy is $250+ per 50-minute session, with a recommended 90-minute couples intake at $375. Family therapy is $275 per 50-minute session, with a recommended 90-minute family intake at $375. In-office appointments are available Saturdays only, and in-office rates may vary.",
  },
  {
    q: "What is your cancellation policy?",
    a: "I ask for at least 48 hours' notice to cancel or reschedule. Late cancellations carry a $150 fee ($800 for a couples intensive, since the day is reserved just for you). That said, I understand emergencies can't be predicted — when something unexpected happens, just reach out and we will work it out together with flexibility.",
  },
  {
    q: "Do you accept insurance or provide superbills?",
    a: "I work with Lyra Health and Spring Health — if your employer offers mental-health benefits through either, your sessions may be covered. I also accept Health Savings Account (HSA/FSA) cards. For private-pay clients, I provide superbills you can submit to your insurance for possible out-of-network reimbursement.",
  },
  {
    q: "Do you offer therapy in Spanish?",
    a: "Sí — hablo español. I provide individual, couples and family therapy in Spanish, and couples sessions can move between English and Spanish in whatever way feels most natural for each partner.",
  },
  {
    q: "Do you offer a consultation before beginning therapy?",
    a: "Yes. I offer a free 20-minute consultation — an opportunity to talk about what is bringing you in, what you are hoping to change, and whether I may be the right therapist for you, before you commit to anything. You can request one through the form on the contact page or book directly online through my Therapy Finder profile.",
  },
  {
    q: "What happens after I submit a consultation request?",
    a: "I personally review every request and respond within one to two business days using your preferred contact method. If we seem like a good fit, we schedule your consultation. If not, I will do my best to point you toward a more appropriate resource.",
  },
];

const TelehealthFaq = () => {
  usePageMeta(
    "Telehealth & FAQ — Online Therapy Across California | Holistic Healing Psychotherapy",
    "Secure online couples and individual therapy anywhere in California. Answers about fees, insurance, sessions, consultations and how telehealth works."
  );

  return (
    <div data-testid="telehealth-faq-page">
      <section className="bg-cream-50 pt-14 pb-16 lg:pt-24 lg:pb-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <p className="eyebrow mb-6" data-testid="faq-eyebrow">Telehealth & FAQ</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] text-ink" data-testid="faq-headline">
              Therapy that fits into <em className="text-forest-700">real life</em>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-sage leading-relaxed max-w-2xl" data-testid="faq-subcopy">
              Secure online sessions for couples and individuals anywhere in California — plus honest answers to the
              practical questions everyone asks before starting.
            </p>
          </FadeIn>

          <div className="mt-12 grid sm:grid-cols-3 gap-4" data-testid="telehealth-highlights">
            {[
              { icon: Video, label: "Secure video sessions" },
              { icon: MapPin, label: "Anywhere in California" },
              { icon: ShieldCheck, label: "Privacy-first practice" },
            ].map(({ icon: Icon, label }, i) => (
              <FadeIn key={label} delay={i * 0.08}>
                <div className="flex items-center gap-3 rounded-xl border border-forest-800/10 bg-cream-100 px-5 py-4">
                  <Icon size={18} className="text-gold-500 shrink-0" />
                  <span className="text-sm font-medium text-forest-800">{label}</span>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-forest-900 py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <SectionHeading dark eyebrow="Investment & Policies" title="Clear fees. No surprises." />
          <FadeIn delay={0.1}>
            <div className="mt-10" data-testid="fees-table">
              {[
                { s: "Individual Therapy", d: "Virtual · 50 minutes", p: "$220" },
                { s: "Couples Therapy", d: "50 minutes", p: "$250+" },
                { s: "Couples Intake", d: "90 minutes · recommended", p: "$375" },
                { s: "Family Therapy", d: "50 minutes", p: "$275" },
                { s: "Family Intake", d: "90 minutes · recommended", p: "$375" },
              ].map((f, i) => (
                <div
                  key={f.s}
                  className="flex items-baseline justify-between gap-6 py-5 border-t border-cream-50/10"
                  data-testid={`fee-row-${i + 1}`}
                >
                  <div>
                    <p className="font-serif text-xl sm:text-2xl text-cream-50">{f.s}</p>
                    <p className="text-xs uppercase tracking-[0.2em] text-cream-50/50 mt-1">{f.d}</p>
                  </div>
                  <p className="font-serif text-2xl sm:text-3xl text-gold-400 shrink-0">{f.p}</p>
                </div>
              ))}
              <p className="pt-6 text-sm text-cream-50/55 leading-relaxed" data-testid="fees-inoffice-note">
                In-office appointments are available on Saturdays only; in-office rates may vary. Fees are due at the
                time of service. HSA/FSA cards accepted, and superbills are provided for private-pay clients on
                request. Evening and weekend appointments are available for busy schedules.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.15}>
            <div className="mt-8 rounded-xl border border-gold-500/30 bg-forest-800/60 p-7" data-testid="cancellation-policy">
              <p className="eyebrow mb-3">Cancellation Policy</p>
              <p className="text-sm text-cream-50/75 leading-relaxed">
                Please provide at least 48 hours' notice to cancel or reschedule. Late cancellations carry a $150
                fee ($800 for a couples intensive, since the day is reserved just for you). That said — I understand
                emergencies can't be predicted. When something unexpected happens, just
                reach out and we will work it out together with flexibility and care.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-cream-50 py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <FadeIn>
            <p className="eyebrow" data-testid="insurance-eyebrow">Insurance & EAP</p>
            <div className="gold-rule mx-auto mt-4 mb-6" />
            <h2 className="font-serif text-3xl sm:text-4xl text-forest-800 tracking-tight" data-testid="insurance-heading">
              Proudly working with
            </h2>
            <p className="mt-4 text-sm sm:text-base text-sage leading-relaxed max-w-xl mx-auto" data-testid="insurance-copy">
              If your employer offers mental-health benefits through Lyra Health or Spring Health, your sessions may
              be covered. I also accept HSA/FSA cards and provide superbills for private-pay clients.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6">
              <div className="rounded-2xl border border-forest-800/10 bg-white px-10 py-7 shadow-[0_10px_30px_rgba(18,55,42,0.06)]" data-testid="insurance-logo-lyra">
                <img src="/images/lyra-health-logo.svg" alt="Lyra Health" className="h-8 sm:h-9 w-auto" />
              </div>
              <div className="rounded-2xl border border-forest-800/10 bg-white px-10 py-7 shadow-[0_10px_30px_rgba(18,55,42,0.06)]" data-testid="insurance-logo-spring">
                <img src="/images/spring-health-logo.svg" alt="Spring Health" className="h-8 sm:h-9 w-auto" />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-cream-100 py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="Common Questions" title="Before you reach out." />
          <FadeIn delay={0.15}>
            <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="faq-accordion">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="rounded-xl border border-forest-800/10 bg-cream-50 px-6 data-[state=open]:border-gold-500/50 transition-colors duration-300"
                >
                  <AccordionTrigger
                    className="text-left font-serif text-lg sm:text-xl text-forest-800 hover:text-gold-600 hover:no-underline py-5"
                    data-testid={`faq-question-${i + 1}`}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-sage leading-relaxed pb-6" data-testid={`faq-answer-${i + 1}`}>
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeIn>
          <FadeIn delay={0.2}>
            <Link
              to="/contact"
              data-testid="faq-schedule-cta"
              className="inline-flex items-center gap-2 mt-12 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:bg-forest-700"
            >
              Schedule a Consultation <ArrowRight size={16} />
            </Link>
          </FadeIn>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default TelehealthFaq;
